import {
    ActionFunctionArgs,
    Form,
    LoaderFunctionArgs,
    redirect,
    useLoaderData,
} from "react-router-dom";
import toast from "react-hot-toast";
import IndeterminateCheckbox from "../../../components/inputs/IndeterminantCheckbox";
import {Input} from "../../../components/inputs/Input";
import PriceInput from "../../../components/inputs/PriceInput";
import {FormLayout} from "../../../layout/FormLayout";
import {createApolloClient} from "../../../providers/ApolloClientFactory";
import {Categories} from "../../../types/item";
import {GET_ITEM_CATEGORIES, GET_ORDER_AHEAD_ITEM} from "./queries";
import {CREATE_ORDER_AHEAD_ITEM} from "./mutations";
import {useEffect, useState} from "react";
import {userStore} from "../../../store/user";
import CustomSelect from "../../../components/customSelect";
import {
    GET_LIST_EVENTS_BY_ORGANIZATION, GET_LIST_VENDORS_BY_ORGANIZATION,
} from "../../../queries";
import {organizationStore} from "../../../store/organization";
import {Event} from "../../../types/event";
import TicketingItemHTML from "../components/TickingItemHTML";
import {Vendor} from "../../users/type";
import { round } from "lodash";
import {ModifierGroup, OrderAheadItem} from "../../../types/orderAhead";
import {PrimaryButton, SecondaryButton, TertiaryButton} from "../../../components/Button";
import CompactThumbUploader from "../../../components/CompactThumbUploader";


const client = createApolloClient();

async function blobToDataURL(blob: Blob): Promise<string> {
    return await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = reject;
        fr.readAsDataURL(blob);
    });
}

function dataUrlToRawBase64(dataUrl: string): string {
    const [, raw = ""] = dataUrl.split(",", 2);
    return raw;
}
const {getState} = userStore;
const {getState: getOrganizationStore} = organizationStore;

export const orderAheadItemCloneLoader = async ({params}: LoaderFunctionArgs) => {
    const organizationId = getOrganizationStore().organizationId;
    const user = getState().user;

    let eventWhere = {};
    let vendorWhere = {};

    if (user?.vendors) {
        vendorWhere = {
            ...vendorWhere,
            id: {
                _in: user?.vendors,
            },
        };
    }

    if (user?.events) {
        eventWhere = {
            ...eventWhere,
            id: {
                _in: user?.events,
            },
        };
    }

    if (organizationId) {
        vendorWhere = {
            ...vendorWhere,
            organization_id: {
                _eq: organizationId,
            },
        };
        eventWhere = {
            ...eventWhere,
            organization_id: {_eq: organizationId},
            is_active: {_eq: true},
        };
    }
    const {id} = params;
    const [
        {data: itemData},
        {data: vendorData},
        {data: eventData},
        {data: itemCategoriesData},
    ] = await Promise.all([
        client.query({
            query: GET_ORDER_AHEAD_ITEM,
            variables: {id},
        }),
        client.query({
            query: GET_LIST_VENDORS_BY_ORGANIZATION,
            variables: {
                where: vendorWhere,
            },
        }),
        client.query({
            query: GET_LIST_EVENTS_BY_ORGANIZATION,
            variables: {
                where: eventWhere,
            },
        }),
        client.query({
            query: GET_ITEM_CATEGORIES,
            variables: {
                where: organizationId
                    ? {
                        organization_id: {
                            _in: [0, organizationId],
                        },
                        is_active: {
                            _eq: true,
                        },
                        reference_type: {
                            _eq: "item_category",
                        },
                    }
                    : {},
            },
        }),
    ]);

    return {
        item: itemData.items_order_ahead_by_pk,
        vendors: vendorData.vendors,
        events: eventData.events,
        itemCategories: itemCategoriesData.dashboard_reference,
    };
};

export const orderAheadItemCloneAction = async ({
                                                  request,
                                              }: ActionFunctionArgs) => {
    const user = getState().user;
    const body = await request.formData();
    const applyInclusivePrice = body.get("tax_type") === "exclusive" ? true : false
    const inclusivePriceString = body.get("inclusive_price") as string;
    const inclusivePrice = parseFloat(inclusivePriceString.length ? inclusivePriceString : "0");
    const priceString = body.get("price") as string;
    const price = parseFloat(priceString.length ? priceString : "0");
    const taxPercentageString = body.get("tax_percentage") as string;
    const taxPercentage = (parseFloat(taxPercentageString) / 100).toFixed(8);
    const modifiers = JSON.parse(body.get("modifiers_json") as string);


    let input = {
        name: body.get("name"),
        is_active: body.get("is_active"),
        available_public_sale: body.get("available_public_sale"),
        event_id: body.get("event_id"),
        vendor_id: body.get("vendor_id"),
        category: body.get("category"),
        description: body.get("description"),
        additional_email_description: body.get("additional_email_description"),
        price: Math.round((applyInclusivePrice ? price : inclusivePrice) * 100),
        tax_type: body.get("tax_type"),
        tax_percentage: taxPercentage ? taxPercentage : 0,
        modifiers: modifiers || [],
        image: body.get("image") || null,
        last_updated_by: user?.id,
    };

    try {
        await client.mutate({
            mutation: CREATE_ORDER_AHEAD_ITEM,
            variables: {
                input,
            },
        });
        toast.success("Order Ahead Item Cloned");
        return redirect(`/order-ahead/overview`);
    } catch (error) {
        toast.error("Failed to Clone Order Ahead Item" + error);
    }
};

export const OrderAheadItemClone = () => {
    const {item, vendors, events, itemCategories} = useLoaderData() as {
        item: OrderAheadItem;
        vendors: Vendor[];
        events: Event[];
        itemCategories: Categories[];
    };
    const [price, setPrice] = useState<number>(
        item.tax_type === 'exclusive'
            ? item.price / 100
            : Math.round(
            ((item.price + item.tax_percentage * item.price) / 100) * 100
        ) / 100
    );
    const [taxPercentage, setTaxPercentage] = useState(
        Number((item.tax_percentage * 100).toFixed(4))
    );
    const [content, setContent] = useState<string>(item.description);
    const [additionalContent, setAdditionalContent] = useState<string>(item.additional_email_description);
    const [taxType, setTaxType] = useState(item.tax_type);
    const [inclusivePrice, setInclusivePrice] = useState<number>(0);
    const [grandTotal, setGrandTotal] = useState(0);
    const [totalTax, setTotalTax] = useState(0);
    const [modifiers, setModifiers] = useState<ModifierGroup[]>(item.modifiers);
    const [thumbBase64, setThumbBase64] = useState<string>(item.image ?? "");

    const handleThumbReady = async (blob: Blob) => {
        const dataUrl = await blobToDataURL(blob);
        setThumbBase64(dataUrlToRawBase64(dataUrl));
    };
    const handleClearImage = () => setThumbBase64("");

    const handlePriceChange = (value: number) => {
        setPrice(value);
    };
    const handleTaxTypeChange = (value: string) => {
        setTaxType(value);
    };
    const handleTaxPercentageChange = (value: string | number | undefined) => {
        if (typeof value === "string") {
            setTaxPercentage(parseFloat(value) || 0);
        }
    };

    const calculateTotals = () => {
        let totalLineItemTax = 0;
        const mainTaxPercentage = taxPercentage || 0;

        if (price === 0) {
            setInclusivePrice(0);
            setTotalTax(0);
            setGrandTotal(0);
            return;
        }

        if (taxType === "inclusive") {
            const mainTaxRate = mainTaxPercentage / 100;
            const lineItemTaxRate = totalLineItemTax / price;
            const combinedTaxRate = mainTaxRate + lineItemTaxRate;
            setInclusivePrice( round((price / (1 + combinedTaxRate)),2));
            const calculatedTax = round((price - inclusivePrice),2);
            setTotalTax(calculatedTax);
            setGrandTotal(price);
        } else {
            const mainTax = (price * mainTaxPercentage) / 100;
            const totalTax = mainTax + totalLineItemTax;
            setTotalTax(totalTax);
            setGrandTotal(price + totalTax);
        }
    };

    useEffect(() => {
        calculateTotals();
    }, [taxType, price, taxPercentage, calculateTotals]);

    const clonedTicketingItemName = `${item.name} - clone`;
    return (
        <Form
            method="post"
            action={`/order-ahead-items/${item.id}/clone`}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                }
            }}
        >
            <FormLayout>
                <div className="col-span-5 sm:col-span-3">
                    <Input
                        label="Order Ahead Item Name"
                        name="name"
                        required
                        defaultValue={clonedTicketingItemName}
                    />
                </div>
                <IndeterminateCheckbox
                    label="Is Active?"
                    name="is_active"
                    defaultChecked={item.is_active}
                />
                <div className="col-span-2">
                    <IndeterminateCheckbox
                        label="Available for Public Sale? -uncheck for Comp Items"
                        name="available_public_sale"
                        defaultChecked={item.available_public_sale}
                    />
                </div>
                <div className="col-span-6">
                    <CustomSelect
                        title="Event"
                        name="event_id"
                        required
                        options={[
                            ...(events?.map((e) => ({
                                label: e.name,
                                value: e.id,
                            })) || []),
                        ]}
                        defaultValue={item.event_id}
                    />
                </div>
                <div className="col-span-6">
                    <CustomSelect
                        title="Vendor"
                        name="vendor_id"
                        required
                        options={[
                            {
                                value: 0,
                                label: "All Vendors",
                            },
                            ...(vendors?.map((v) => ({
                                label: v.name,
                                value: v.id,
                            })) || []),
                        ]}
                        defaultValue={item.vendor_id}
                    />
                </div>
                <div className="col-span-6">
                    <CustomSelect
                        title="Item Category"
                        name="category"
                        required
                        options={[
                            ...(itemCategories?.map((v) => ({
                                label: v.reference_label,
                                value: v.reference_value,
                            })) || []),
                        ]}
                        defaultValue={item.category}
                    />
                </div>
                <div className="col-span-6">
                    <TicketingItemHTML
                        content={content}
                        setContent={setContent}
                        label="Description"
                    />
                    <Input type="hidden" name="description" value={content} />
                </div>
                <div className="col-span-6">
                    <TicketingItemHTML
                        content={additionalContent}
                        setContent={setAdditionalContent}
                        label="Additional Email Confirmation Description"
                    />
                    <Input type="hidden" name="additional_email_description" value={additionalContent} />
                </div>
                <div className="col-span-6">
                    <h3 className="text-lg font-semibold mb-2 text-gray-700">Item Image</h3>
                    <div className="flex items-center gap-4">
                        {thumbBase64 ? (
                            <div className="relative w-[125px] h-[125px]">
                                <img
                                    src={`data:image/webp;base64,${thumbBase64}`}
                                    width={125}
                                    height={125}
                                    alt="Item"
                                    className="rounded border border-slate-200 object-cover w-[125px] h-[125px]"
                                />
                                <button
                                    type="button"
                                    onClick={handleClearImage}
                                    aria-label="Remove image"
                                    title="Remove image"
                                    className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#f04345] text-white flex items-center justify-center shadow ring-1 ring-white/60 hover:opacity-90"
                                >
                                    ×
                                </button>
                            </div>
                        ) : (
                            <div className="w-[125px] h-[125px] rounded border border-dashed border-slate-300 flex items-center justify-center text-xs text-slate-400">
                                No image
                            </div>
                        )}
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center">
                            Upload Image
                            <CompactThumbUploader onReady={handleThumbReady} />
                        </h3>
                    </div>
                </div>
                <div className="col-span-5 sm:col-span-2">
                    <PriceInput
                        label="Price"
                        name="price"
                        onChange={(value) => handlePriceChange(value ? value : 0)}
                        value={price}
                        required
                    />
                </div>
                <div className="col-span-6 sm:col-span-2">
                    <CustomSelect
                        title="Tax Type"
                        name="tax_type"
                        options={[
                            {label: "Inclusive", value: "inclusive"},
                            {label: "Exclusive", value: "exclusive"},
                        ]}
                        defaultValue={item.tax_type}
                        onChange={handleTaxTypeChange}
                    />
                </div>
                <div className="col-span-6 sm:col-span-2">
                    <Input
                        label="Tax % ( 7.5 = 7.5% )"
                        name="tax_percentage"
                        required
                        onChange={handleTaxPercentageChange}
                        defaultValue={taxPercentage}
                    />
                </div>
                <div className="col-span-6 text-gray-700">
                    <h3 className="text-lg font-semibold">Price Preview</h3>
                    <div>Price: ${taxType === "inclusive" ? inclusivePrice.toFixed(2) : price.toFixed(2)}</div>
                    <div>Total Tax: ${totalTax.toFixed(2)}</div>
                    <div>Grand Total: ${grandTotal.toFixed(2)}</div>
                </div>
                <div className="col-span-6">
                    <h3 className="text-lg font-semibold mb-2 text-gray-700">Item Modifiers</h3>
                    {modifiers.map((mod, groupIdx) => (
                        <div key={groupIdx} className="border rounded-lg p-4 mb-4 bg-gray-50 grid grid-cols-6 gap-4">
                            <div className="col-span-5">
                                <Input
                                    label="Modifier Group Name"
                                    name="modifier_group_name"
                                    defaultValue={mod.name}
                                    onChange={(val) => {
                                        const updated = structuredClone(modifiers);
                                        updated[groupIdx].name = val as string;
                                        setModifiers(updated);
                                    }}
                                />
                            </div>
                            <div className="col-span-1 flex items-end">
                                <PrimaryButton
                                    type="button"
                                    onClick={() => {
                                        const updated = structuredClone(modifiers);
                                        updated.splice(groupIdx, 1);
                                        setModifiers(updated);
                                    }}
                                >
                                    Remove Group
                                </PrimaryButton>
                            </div>

                            <div className="col-span-2">
                                <CustomSelect
                                    title="Selection Type"
                                    defaultValue={mod.type}
                                    onChange={(val) => {
                                        const updated = structuredClone(modifiers);
                                        updated[groupIdx].type = val;
                                        setModifiers(updated);
                                    }}
                                    options={[
                                        { label: "Single", value: "single" },
                                        { label: "Multi", value: "multi" },
                                    ]}
                                />
                            </div>

                            <div className="col-span-2 mt-0.5">
                                <IndeterminateCheckbox
                                    label="Required"
                                    name={`mod-required-${groupIdx}`}
                                    defaultChecked={mod.required}
                                    onChange={(val) => {
                                        const updated = structuredClone(modifiers);
                                        updated[groupIdx].required = val as boolean;
                                        setModifiers(updated);
                                    }}
                                />
                            </div>

                            <div className="col-span-6">
                                <h4 className="font-semibold text-md mb-2 text-gray-700">Options</h4>
                                {mod.options.map((opt, optIdx) => (
                                    <div key={optIdx} className="grid grid-cols-6 gap-4 mb-4 items-end">
                                        <div className="col-span-2">
                                            <Input
                                                label="Option Name"
                                                name="option_name"
                                                defaultValue={opt.label}
                                                onChange={(val) => {
                                                    const updated = structuredClone(modifiers);
                                                    updated[groupIdx].options[optIdx].label = val as string;
                                                    setModifiers(updated);
                                                }}
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <PriceInput
                                                label="Additional Price"
                                                name="additional_price"
                                                value={opt.price / 100} // Convert cents → dollars for display
                                                onChange={(val) => {
                                                    const updated = structuredClone(modifiers);
                                                    updated[groupIdx].options[optIdx].price = Math.round((val || 0) * 100); // Store in cents
                                                    setModifiers(updated);
                                                }}
                                            />
                                        </div>
                                        <div className="col-span-1 mb-4">
                                            <IndeterminateCheckbox
                                                label="Default Selected"
                                                name="default_selected"
                                                defaultChecked={opt.default_selected}
                                                onChange={(val) => {
                                                    const updated = structuredClone(modifiers);
                                                    updated[groupIdx].options[optIdx].default_selected = val as boolean;
                                                    setModifiers(updated);
                                                }}
                                            />
                                        </div>
                                        <div className="col-span-1 flex items-end">
                                            <TertiaryButton
                                                type="button"
                                                onClick={() => {
                                                    const updated = structuredClone(modifiers);
                                                    updated[groupIdx].options.splice(optIdx, 1);
                                                    setModifiers(updated);
                                                }}
                                            >
                                                Remove Option
                                            </TertiaryButton>
                                        </div>
                                    </div>
                                ))}
                                <div className="mt-2">
                                    <TertiaryButton
                                        type="button"
                                        onClick={() => {
                                            const updated = structuredClone(modifiers);
                                            updated[groupIdx].options.push({ label: "", price: 0, default_selected: false });
                                            setModifiers(updated);
                                        }}
                                    >
                                        Add Option
                                    </TertiaryButton>
                                </div>
                            </div>
                        </div>
                    ))}
                    <SecondaryButton
                        type="button"
                        onClick={() => {
                            setModifiers((prev) => [
                                ...structuredClone(prev),
                                {
                                    name: "",
                                    type: "single",
                                    required: false,
                                    options: [{ label: "", price: 0, default_selected: false }],
                                },
                            ]);
                        }}
                    >
                        Add Modifier Group
                    </SecondaryButton>
                </div>
                <input
                    type="hidden"
                    value={inclusivePrice.toFixed(2)}
                    name="inclusive_price"
                />
                <input type="hidden" name="modifiers_json" value={JSON.stringify(modifiers)} />
                <input type="hidden" name="image" value={thumbBase64} />
            </FormLayout>
        </Form>
    );
};
